/**
 * HUDV2 - Vertical slice HUD with fixed top slots.
 */

import Phaser from 'phaser';
import { COLORS, FONTS, GAME_WIDTH, SAFE_AREA } from '../config';
import { WeaponType } from '../utils/constants';
import { getHudBounds } from '../utils/responsive';
import { fitImageContain, getWeaponGunTextureKey } from './weaponVisuals';

export interface HUDV2Config {
  soldierCount: number;
  score: number;
  progress: number;
  weapon?: WeaponType;
}

export class HUDV2 extends Phaser.GameObjects.Container {
  private static readonly SIDE_MARGIN = 10;
  private static readonly TOP_PAD = 12;
  private static readonly CARD_HEIGHT = 44;
  private static readonly CARD_RADIUS = 10;
  private static readonly CARD_INNER_PAD_X = 10;
  private static readonly VALUE_BASE_Y = 27;
  private static readonly LABEL_BASE_Y = 11;
  private static readonly CENTER_GAP = 54;
  private static readonly PAUSE_RESERVE = 72;
  private static readonly WEAPON_SLOT_SIZE = 40;

  private readonly soldierBg: Phaser.GameObjects.Graphics;
  private readonly scoreBg: Phaser.GameObjects.Graphics;
  private readonly weaponBg: Phaser.GameObjects.Graphics;

  private readonly soldierValue: Phaser.GameObjects.Text;
  private readonly scoreValue: Phaser.GameObjects.Text;
  private readonly soldierLabel: Phaser.GameObjects.Text;
  private readonly scoreLabel: Phaser.GameObjects.Text;
  private readonly weaponIcon: Phaser.GameObjects.Image;

  private currentSoldiers: number;
  private currentScore: number;
  private currentWeapon: WeaponType;
  private readonly valueTweenTargets: Phaser.GameObjects.Text[] = [];
  private isDestroyed: boolean = false;

  constructor(scene: Phaser.Scene, config: HUDV2Config) {
    super(scene, 0, 0);

    this.currentSoldiers = config.soldierCount;
    this.currentScore = config.score;
    this.currentWeapon = config.weapon ?? WeaponType.Pistol;
    void config.progress;

    this.soldierBg = scene.add.graphics();
    this.scoreBg = scene.add.graphics();
    this.weaponBg = scene.add.graphics();

    this.soldierLabel = scene.add.text(0, 0, 'ARMY', {
      fontFamily: FONTS.body,
      fontSize: '11px',
      color: '#DCE5F2',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);

    this.soldierValue = scene.add.text(0, 0, this.formatNumber(this.currentSoldiers), {
      fontFamily: FONTS.display,
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    });
    this.soldierValue.setOrigin(0, 0.5);

    this.scoreLabel = scene.add.text(0, 0, 'SCORE', {
      fontFamily: FONTS.body,
      fontSize: '11px',
      color: '#DCE5F2',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5);

    this.scoreValue = scene.add.text(0, 0, this.formatNumber(this.currentScore), {
      fontFamily: FONTS.display,
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    });
    this.scoreValue.setOrigin(1, 0.5);
    this.weaponIcon = scene.add.image(0, 0, getWeaponGunTextureKey(this.currentWeapon)).setOrigin(0.5);

    this.add(this.soldierBg);
    this.add(this.scoreBg);
    this.add(this.weaponBg);
    this.add(this.soldierLabel);
    this.add(this.soldierValue);
    this.add(this.scoreLabel);
    this.add(this.scoreValue);
    this.add(this.weaponIcon);
    this.valueTweenTargets.push(this.soldierValue, this.scoreValue);

    this.layout();

    this.setDepth(60);
    scene.add.existing(this);

    scene.scale.on('resize', this.layout, this);
  }

  updateSoldierCount(count: number): void {
    this.currentSoldiers = Math.max(0, Math.round(count));
    this.soldierValue.setText(this.formatNumber(this.currentSoldiers));
    this.playValuePulse(this.soldierValue);
    this.layout();
  }

  updateScore(score: number): void {
    this.currentScore = Math.max(0, Math.round(score));
    this.scoreValue.setText(this.formatNumber(this.currentScore));
    this.playValuePulse(this.scoreValue);
    this.layout();
  }

  updateProgress(progress01: number): void {
    void progress01;
  }

  updateWeapon(weapon: WeaponType): void {
    if (weapon === this.currentWeapon) return;
    this.currentWeapon = weapon;
    this.weaponIcon.setTexture(getWeaponGunTextureKey(weapon));
    this.layout();
    this.playValuePulse(this.weaponIcon);
  }

  getSoldierCount(): number {
    return this.currentSoldiers;
  }

  private layout(): void {
    const hudBounds = getHudBounds(58);
    const cardTop = Math.max(SAFE_AREA.top + 8, hudBounds.top + HUDV2.TOP_PAD);
    const leftCardX = HUDV2.SIDE_MARGIN;

    const scoreRightLimit = GAME_WIDTH - HUDV2.PAUSE_RESERVE;
    const centerLeftLimit = GAME_WIDTH / 2 - HUDV2.CENTER_GAP / 2;
    const centerRightLimit = GAME_WIDTH / 2 + HUDV2.CENTER_GAP / 2;

    const leftTextWidth = Math.ceil(this.soldierValue.width);
    const leftRawWidth = leftTextWidth + HUDV2.CARD_INNER_PAD_X * 2;
    const leftMaxWidth = Math.max(96, centerLeftLimit - leftCardX);
    const soldierWidth = Phaser.Math.Clamp(leftRawWidth, 96, leftMaxWidth);

    const leftTextX = Math.round(leftCardX + HUDV2.CARD_INNER_PAD_X);
    this.soldierLabel.setPosition(leftTextX, Math.round(cardTop + HUDV2.LABEL_BASE_Y));
    this.soldierValue.setPosition(leftTextX, Math.round(cardTop + HUDV2.VALUE_BASE_Y));

    this.soldierBg.clear();
    this.soldierBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.soldierBg.fillRoundedRect(leftCardX, cardTop, soldierWidth, HUDV2.CARD_HEIGHT, HUDV2.CARD_RADIUS);
    this.soldierBg.lineStyle(1, 0xffffff, 0.14);
    this.soldierBg.strokeRoundedRect(leftCardX, cardTop, soldierWidth, HUDV2.CARD_HEIGHT, HUDV2.CARD_RADIUS);

    const scoreTextWidth = Math.ceil(this.scoreValue.width);
    const scoreRawWidth = scoreTextWidth + HUDV2.CARD_INNER_PAD_X * 2;
    const scoreMaxWidth = Math.max(96, scoreRightLimit - centerRightLimit);
    const scoreWidth = Phaser.Math.Clamp(scoreRawWidth, 96, scoreMaxWidth);
    const scoreCardX = scoreRightLimit - scoreWidth;
    const textRight = Math.round(scoreCardX + scoreWidth - HUDV2.CARD_INNER_PAD_X);
    this.scoreLabel.setPosition(textRight, Math.round(cardTop + HUDV2.LABEL_BASE_Y));
    this.scoreValue.setPosition(textRight, Math.round(cardTop + HUDV2.VALUE_BASE_Y));

    this.scoreBg.clear();
    this.scoreBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.scoreBg.fillRoundedRect(
      scoreCardX,
      cardTop,
      scoreWidth,
      HUDV2.CARD_HEIGHT,
      HUDV2.CARD_RADIUS
    );
    this.scoreBg.lineStyle(1, 0xffffff, 0.14);
    this.scoreBg.strokeRoundedRect(
      scoreCardX,
      cardTop,
      scoreWidth,
      HUDV2.CARD_HEIGHT,
      HUDV2.CARD_RADIUS
    );

    const slotSize = HUDV2.WEAPON_SLOT_SIZE;
    const slotX = Math.round(GAME_WIDTH / 2 - slotSize / 2);
    const slotY = Math.round(cardTop + (HUDV2.CARD_HEIGHT - slotSize) / 2);
    this.weaponBg.clear();
    this.weaponBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.weaponBg.fillRoundedRect(slotX, slotY, slotSize, slotSize, 10);
    this.weaponBg.lineStyle(1, 0xffffff, 0.14);
    this.weaponBg.strokeRoundedRect(slotX, slotY, slotSize, slotSize, 10);
    this.weaponIcon.setPosition(slotX + slotSize / 2, slotY + slotSize / 2);
    fitImageContain(this.weaponIcon, slotSize - 9, slotSize - 17);
  }

  private playValuePulse(target: Phaser.GameObjects.Text | Phaser.GameObjects.Image): void {
    this.scene.tweens.killTweensOf(target);
    target.setScale(1);
    this.scene.tweens.add({
      targets: target,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 90,
      ease: 'Back.easeOut',
      yoyo: true,
    });
  }

  private formatNumber(value: number): string {
    return Math.max(0, Math.round(value)).toLocaleString('en-US');
  }

  override destroy(fromScene?: boolean): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.scene?.scale?.off('resize', this.layout, this);
    this.valueTweenTargets.forEach((target) => this.scene?.tweens?.killTweensOf(target));
    this.scene?.tweens?.killTweensOf(this.weaponIcon);
    super.destroy(fromScene);
  }
}
